import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { LinearProgress } from '@material-ui/core';
import { useForm, FormProvider } from 'react-hook-form';

import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { selectUser } from 'entities/user/selectors';
import { selectSDGs } from 'entities/cause/selectors';
import { Privileges } from 'utils/privileges';
import { Features } from 'utils/features';
import Box from 'retired/Box';
import {
  useActionTemplateResultsQuery,
  ActionVisibilityStatus,
  MutationMutationUpsertActionTemplateArgs,
  IncentiveInputType,
  IncentiveType,
  ActionTemplate,
} from 'generated/graphql';
import ActionForm from 'components/Actions/ActionForm';
import {
  FormFieldValues,
  DEFAULT_REWARD_UNIT,
  DEFAULT_USD_REWARD_AMOUNT,
} from 'components/Actions/types';

import epics from './epics';
import reducer from './reducer';
import { selectLoading } from './selectors';
import { initActionTemplate } from './types';
import { useActionTemplateMutation } from './useActionTemplateMutation';

const key = 'actionTemplatePage';

const getDefaultValues = (actionTemplate?: ActionTemplate): FormFieldValues => {
  if (!actionTemplate) {
    return {
      title: '',
      subTitle: '',
      description: '',
      bannerImage: '',
      rewardAmount: DEFAULT_USD_REWARD_AMOUNT,
      rewardUnit: DEFAULT_REWARD_UNIT,
      impactUnit: '',
      impactAmount: 0,
      causes: [],
      categories: [],
      sdgs: [],
      status: 'draft',
      public: false,
    };
  }
  const categories = actionTemplate.Causes?.filter((cause) => cause.type === 'Category').map(
    (c) => c.id
  );
  const sdgs = actionTemplate.Causes?.filter((cause) => cause.type === 'SDG').map((c) => c.id);

  const reward = actionTemplate.incentives?.find((i) => i.type === IncentiveType.Reward);
  const { unit: rewardUnit, amount: rewardAmount } = reward ?? {
    unit: DEFAULT_REWARD_UNIT,
    amount: DEFAULT_USD_REWARD_AMOUNT,
  };

  const impact = actionTemplate.incentives?.find((i) => i.type === IncentiveType.Impact);

  return {
    title: actionTemplate.title,
    subTitle: actionTemplate.subTitle || '',
    description: actionTemplate.description || '',
    bannerImage: actionTemplate.bannerImage || '',
    rewardUnit,
    rewardAmount,
    impactUnit: impact?.unit || '',
    impactAmount: impact?.amount || 0,
    causes: actionTemplate.Causes,
    categories,
    sdgs,
    status: actionTemplate.status,
    public: actionTemplate.public ?? false,
  };
};

const buildActionTemplatePayload = ({
  id,
  formValues,
}: {
  id?: string;
  formValues: FormFieldValues;
}): MutationMutationUpsertActionTemplateArgs => {
  const causesIds = [...(formValues.categories || []), ...(formValues.sdgs || [])];

  const incentives: IncentiveInputType[] = [];

  if (formValues.rewardAmount && formValues.rewardUnit) {
    incentives.push({
      type: IncentiveType.Reward,
      unit: formValues.rewardUnit,
      amount: Number(formValues.rewardAmount),
    });
  }
  if (formValues.impactAmount && formValues.impactUnit) {
    incentives.push({
      type: IncentiveType.Impact,
      unit: formValues.impactUnit,
      amount: Number(formValues.impactAmount),
    });
  }

  return {
    data: {
      id,
      status: formValues.status as ActionVisibilityStatus,
      title: formValues.title,
      subTitle: formValues.subTitle,
      description: formValues.description,
      bannerImage: formValues.bannerImage,
      incentives,
      causes: causesIds,
      public: formValues.public,
    },
  };
};

const ActionTemplatePage: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const actionTemplateId = id !== 'new' ? id : undefined;

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });

  const user = useSelector(selectUser);
  const SDGs = useSelector(selectSDGs);

  const isActionsEnabled =
    user && user.hasFeature(Features.ACTIONS) && user.hasPrivilege(Privileges.ACTIONS_MANAGE);

  const actionTemplateQuery = useActionTemplateResultsQuery({
    variables: { templateId: actionTemplateId },
    skip: !isActionsEnabled || !actionTemplateId,
  });

  const [actionTemplateMutation, { loading: loadingActionMutation }] =
    useActionTemplateMutation(actionTemplateId);

  const actionTemplate = actionTemplateQuery.data?.actionTemplateResults?.templates[0];

  const formMethods = useForm<FormFieldValues>({
    mode: 'all',
    defaultValues: getDefaultValues(actionTemplate),
  });

  const handleSubmit = formMethods.handleSubmit((formValues: FormFieldValues) => {
    const data = buildActionTemplatePayload({
      id: actionTemplateId,
      formValues,
    });

    const isPublishing = formMethods?.getValues()?.status === 'published';
    if (!formMethods?.formState?.isValid && isPublishing) {
      alert('Some required fields are missing. You can save it as a draft for now.');
    } else {
      actionTemplateMutation(data);
    }
  });

  const loading = useSelector(selectLoading) || actionTemplateQuery.loading;

  useEffect(() => {
    dispatch(initActionTemplate());
  }, [dispatch]);

  useEffect(() => {
    formMethods.reset(getDefaultValues(actionTemplate));
  }, [actionTemplate, formMethods]);

  useEffect(() => {
    if (actionTemplate) {
      formMethods?.trigger();
    }
  }, [actionTemplate]);

  if (!isActionsEnabled) {
    history.replace('/');
  }

  if (actionTemplateId && !loading && !actionTemplate) {
    history.replace('/notfound');
  }

  return (
    <>
      {loading ? (
        <Box>
          <LinearProgress />
        </Box>
      ) : (
        <FormProvider {...formMethods}>
          <ActionForm
            title="Action Template"
            formMethods={formMethods}
            handleSubmit={handleSubmit}
            isLoading={loadingActionMutation}
            showSDGs={SDGs?.size > 0}
            showDates={false}
            showSelectNonprofit={false}
            showCampaignTip={false}
            showSelectVisibility
            backLink="/actions/templates/browse"
          />
        </FormProvider>
      )}
    </>
  );
};

export default ActionTemplatePage;
